import _ from 'lodash';
import mixin from '../privates/utils/mixin';
import Element from './Element';
import ClickableMixin from './mixins/ClickableMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import reporter from '../privates/reporter';
import * as Utils from './Utils';
import Link from './Link';
import Enums from '../privates/gridEnums';
import * as members from '../privates/privateMembers';
import {validate, validateRange, validateValueFromEnum} from '../privates/validations/validation';
import {nullIfNil} from '../privates/utils';

import imageUtils from '../privates/utils/imageUtils';

const fieldTypeOperatorTypeMapping = {
    [Enums.FieldType.STRING]: 'StringOperator',
    [Enums.FieldType.NUMBER]: 'NumericOperator',
    [Enums.FieldType.DATE]: 'DateOperator'
};

function processRowLinks(rows, columnDefs, linksTarget) {
    const linkPaths = _.reduce(columnDefs, (links, columnDef) => {
        if (columnDef.linkPath) {
            links.push(columnDef.linkPath);
        }
        return links;
    }, []);
    if (linkPaths.length > 0) {
        _.map(rows, (row) => {
            _.forEach(linkPaths, (linkPath) => {
                const url = _.get(row, linkPath);
                let linkData;
                try {
                    const link = new Link(url, linksTarget);
                    linkData = link.getData();
                } catch (ex) {
                    if (!_.isEmpty(url)) {
                        reporter.reportSdkError(`Error: couldn\'t process link with url "${url}": ${ex.message}`);
                    }
                    linkData = {};
                } finally {
                    const linkObjPath = `${linkPath}_linkObj`;
                    _.set(row, linkObjPath, linkData);
                }
            });
        });
    }
    return rows;
}

function processRowImageURIs(rows, columnDefs) {
    const imageColumns = _.filter(columnDefs, ({type}) => type === Enums.FieldType.IMAGE);
    _.forEach(imageColumns, (columnDef) => {
        _.forEach(rows, (row) => {
            const src = _.get(row, columnDef.dataPath);
            if (!_.isNil(src)) {
                const imageData = imageUtils.extractModelDataFromSrc(src);
                if (!_.isNil(imageData)) {
                    _.set(row, columnDef.dataPath, imageData);
                }
            }
        });
    });
    return rows;
}

function validateDates(rows, columnDefs) {
    const dateColumns = _.filter(columnDefs, {type: Enums.FieldType.DATE});
    _.forEach(dateColumns, (columnDef) => {
        _.forEach(rows, (row) => {
            const dateValue = _.get(row, columnDef.dataPath);
            if (!_.isNil(dateValue)) {
                const date = new Date(dateValue);
                if (isNaN(date)) {
                    reporter.reportSdkError(`Error: Invalid date: "${dateValue}" in column '"${columnDef.dataPath}"'`);
                }
            }
        });
    });
}

function executeModificationRowBehaviour(delta) {
    const RMI = members._RMI.get(this);
    const compId = members._compId.get(this);
    RMI.executeCompBehavior(compId, 'modifyRow', delta);
}

function _processData(rows, columnDefs, linksTarget) {
    validateDates(rows, columnDefs);
    let processedRows = processRowLinks(rows, columnDefs, linksTarget);
    processedRows = processRowImageURIs(rows, columnDefs);
    return processedRows;
}

function dataFetchHandler(RMI){
    return (e) => {
        const compId = e.syntheticEvent.id;
        const compData = RMI.getData(compId);
        const rowRetrievalFunc = _.get(compData, 'dataFetchHandlers');
        if (!_.isFunction(rowRetrievalFunc)) {
            return;
        }
        rowRetrievalFunc(e.syntheticEvent.startRow,
            e.syntheticEvent.endRow,
            e.syntheticEvent.sorting,
            e.syntheticEvent.filter)
            .then((data) => {
                const props = RMI.getProps(compId);
                const pageRows = _processData(data.pageRows, props.columns, props.linkTarget);
                const prevNumberOfRows = RMI.getData(compId).numberOfDisplayedRows;
                const currNumberOfRows = props.pagination.type === Enums.PaginationType.PAGES ? pageRows.length : prevNumberOfRows;
                RMI.setData(compId, {
                    rows: pageRows,
                    rowsCount: data.totalRowsCount,
                    numberOfDisplayedRows: currNumberOfRows,
                    revision: Date.now()
                });
            });
    };
}

/**
 * @class Table
 * @summary A table for displaying data.
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.ClickableMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.Grid
 * @definitionId wysiwyg.viewer.components.Grid
 */
class Table extends Element {
    // METHODS

    getDataSourceType () {
        return _.get(Utils.getData(this, 'dataSource'), 'type');
    }

    // PROPERTIES:

    /*  REMOVED FROM SDK FOR PLATFORM 1.0
     Leeron Shalev indicated that `sorting` is not going to be included in "the first release"
     * @typedef {external:Object} $w.Table~SortParam
     * @summary An object used by the `sorting` property that contains one set of sorting parameters.
     * @property {external:String} colId The [column]($w.Table~Column) ID to sort
     * @property {ASC|DSC|NONE} direction The sorting direction
     * @see [sorting](#sorting)
     */

    /*  REMOVED FROM SDK FOR PLATFORM 1.0
     Leeron Shalev indicated that `sorting` is not going to be included in "the first release"
     * @member {$w.Table~SortParam[]} sorting
     * @label property
     * @syntax
     * let tableSortParams = $w("#myTable").sorting;
     * $w("#myTable").sorting = tableSortParams;
     * @summary Sets the parameters being used to sort the specified table, or returns the existing sort parameters.
     * @description The `sorting` property is composed of an array of {@link $w.Table~SortParam|`SortParam[]`} objects.
     *  The order of the objects in the array determines the order in which the columns are sorted. ___Not clear if
     *  the first object in the array is sorted last or first i.e. is the primary sort key or the least significant sort key___
     * @note When setting the filter, if the `fieldType` or `operator` provided in [tableFilterParams]($w.Table~FilterParam)
     *  is not valid, an error is printed to the console.
     * @memberof $w.Table
     * @instance
     */
    get sorting() {
        return Utils.getProp(this, 'sorting');
    }

    // param [{colId: String, sort: SortDirection}] sortDef
    /**
     * @ignore
     */
    set sorting(sortDef) {
        const directions = _.map(Enums.SortDirection);
        const compType = members._type.get(this).compType;
        const isArrayValid = validate({
            compType,
            functionName: 'sorting',
            dataToValidate: {sorting: sortDef},
            compName: this.id
        });
        if (!isArrayValid) {
            return;
        }
        let allItemsFromEnum = true;

        _.forEach(sortDef, (colSort, index) => {
            if (!_.isObject(colSort) || !validateValueFromEnum({
                functionName: 'sorting',
                propertyName: 'sortDef',
                value: colSort.sort,
                enumValues: directions,
                itemIndex: index
            })) {
                allItemsFromEnum = false;
            }
        });

        if (!allItemsFromEnum) {
            return;
        }
        Utils.setProp(this, {sorting: sortDef});
    }

    /*  REMOVED FROM SDK FOR PLATFORM 1.0
     Leeron Shalev indicated that `filter` is not going to be included in "the first release"
     * @typedef {external:Object} $w.Table~FilterParam
     * @summary An object used by the `filter` property that contains one set of filter parameters.
     * @property {STRING|NUMBER|DATE|IMAGE|BOOLEAN|RICH_TEXT} fieldType The data type of the field to be filtered. ___Not sure about this...___
     * @property {$w.Table~StringOperator|$w.Table~NumericOperator|$w.Table~DateOperator} operator
     *  The comparison operator to use, such as ENDS_WITH or GREATER_THAN.
     * @property {external:String|external:Number|external:Date} value The value used for the comparison.
     * @see [filter](#filter)
     */

    /*  REMOVED FROM SDK FOR PLATFORM 1.0
     Leeron Shalev indicated that `filter` is not going to be included in "the first release"
     * @member {$w.Table~FilterParam} filter
     * @label property
     * @syntax
     * let tableFilterParams = $w("#myTable").filter;
     * $w("#myTable").filter = tableFilterParams;
     * @summary Sets the parameters being used to filter the specified table, or returns the existing filter parameters.
     * @description ___I'm not sure if by setting this you actually execute the filtering of a table, but it would seem so___
     * @note When setting the filter, if the `fieldType` or `operator` provided in [tableFilterParams]($w.Table~FilterParam) is
     *  not valid, an error is printed to the console.
     * @memberof $w.Table
     * @instance
     */
    get filter() {
        return Utils.getProp(this, 'filter');
    }

    // param {columnId: {type: Enums.FieldType, operator: Table.StringOperator|Table.NumberOperator|Table.DateOperator}} filter
    /**
     * @ignore
     */
    set filter(filter) {
        const fieldTypes = _.map(Enums.FieldType);
        const compType = members._type.get(this).compType;
        const isObj = validate({
            compType,
            functionName: 'filter',
            dataToValidate: {filter},
            compName: this.id
        });
        if (!isObj) {
            return;
        }
        let isValid = true;
        _.forEach(_.map(filter), (currentFilter) => {
            if (!validateValueFromEnum({
                functionName: 'filter',
                propertyName: 'filter',
                value: currentFilter.type,
                enumValues: fieldTypes
            })) {
                isValid = false;
                return;
            }
            const operatorType = fieldTypeOperatorTypeMapping[currentFilter.type];
            if (!validateValueFromEnum({
                functionName: 'filter',
                propertyName: 'filter',
                value: currentFilter.operator,
                enumValues: _.map(Enums.filtering[operatorType])
            })) {
                isValid = false;

            }
        });
        if (!isValid) {
            return;
        }

        Utils.setProp(this, {filter});
    }

    /**
     * @typedef {external:Object} $w.Table~PaginationOptions
     * @summary An object used by the `pagination` property that
     * contains the pagination options which determine if and how the table
     * is paginated.
     * @description
     * The table can paginate in three ways:
     *
     *  + `"normal"`: No pagination. The table is scrollable if there are more
     *    rows than can be displayed at once. All data is fetched at once.
     *  + `"pagination"`: The data is separated into pages which are navigatable
     *    using paging buttons. New data is fetched when a user clicks on a
     *    paging button.
     *  + `"virtual"`: The data is separated into pages which are navigated by
     *    scrolling. New data is fetched when user a user scrolls below the
     *    displayed rows.
     * @property {external:String} type The type of pagination to use: `"normal"`, `"pagination"`, or `"virtual"`.
     * @property {external:Number} rowsPerPage The number of rows per page.
     * @snippet [Table-pagination_get.es6=Get the table's pagination options]
     * @snippet [Table-pagination_set.es6=Set the table's pagination options]
     * @see [pagination](#pagination)
     */

    /**
     * @member pagination
     * @label property
     * @syntax
     * set pagination(): PaginationOptions
     * get pagination(value: PaginationOptions): void
     * @summary Sets or gets the table's pagination options.
     * @description
     *  Setting the `pagination` property sets the table's
     *  [pagination options](#PaginationOptions) and changes the way the table
     *  is paginated.
     *
     *  Getting the `pagination` property returns the table's
     *  current [pagination options](#PaginationOptions).
     * @snippet [Table-pagination_get.es6=Get the table's pagination options]
     * @snippet [Table-pagination_set.es6=Set the table's pagination options]
     * @memberof $w.Table
     * @type {$w.Table~PaginationOptions}
     * @instance
     */
    get pagination() {
        return _.omit(Utils.getProp(this, 'pagination'), ['manualHeightProps']);
    }

  /**
   * @member pagination
   * @memberof $w.Table
   * @param {$w.Table~PaginationOptions} pagination
   */
    set pagination(pagination) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'pagination',
            dataToValidate: {pagination},
            shouldCheckMandatoryFields: true,
            compName: this.id
        })) {
            return;
        }
        Utils.setProp(this, {pagination});
    }

    /**
     * @typedef {external:Object} $w.Table~Column
     * @summary An object used by the `columns` property that
     *  contains the properties that define a table column. Table columns do not
     *  store any data.
     * @property {external:String} id The column ID.
     * @property {external:String} dataPath The location of the data displayed
     *  in the column.
     *
     *  When the table is populated by a connection to a dataset, the `dataPath`
     *  value is a field key from the collection that the dataset is connected to.
     *
     *  When the table is populated by using the [`rows`](#rows) or
     *  [`dataFetcher`](#dataFetcher) properties, the `dataPath` value is one of the
     *  property keys from the table's row objects.
     * @property {external:String} label The column header label.
     * @property {external:String} type The type of data in this column: `"number"`, `"string"`, `"date"`, `"image"`, `"bool"`, or `"richText"`.
     * @property {external:Number} [width] The pixel width of the column.
     * @property {external:Boolean} [visible] Whether the column is visible.
     * @property {external:String} [linkPath] The location of the links used when
     *  the items in the column are clicked.
     *
     *  When the table is populated by a connection to a dataset, the `linkPath`
     *  value is a field key from the collection that the dataset is connected to.
     *  The collection field can be a [regular field](https://support.wix.com/en/article/about-database-collections#regular-fields)
     *  that contains URLs or a [calculated field](https://support.wix.com/en/article/about-database-collections#calculated-fields)
     *  that contains relative links to dynamic pages.
     *
     *  When the table is populated by using the [`rows`](#rows) or
     *  [`dataFetcher`](#dataFetcher) properties, the `linkPath` value is one of the
     *  property keys from the table's rows objects.
     *  The property values associated with that key contain URLs or relative links.
     * @snippet [Table-columns_get.es6=Get the list of columns and the first column's information]
     * @snippet [Table-columns_set.es6=Set the list of columns for a table]
     * @snippet [Table-columns_set_data.es6=Set the columns, data, and links for a table]
     *  This example sets the columns of the table using the table's `columns`
     *  property. The items in the first column are linked using the URLs found
     *  in the `wikiLink` property of the table data. It then populates a table
     *  with static data using the table's `rows` property.
     * @see [columns](#columns)
     */

    /**
     * @member columns
     * @label property
     * @syntax
     * set columns(): Column[]
     * get columns(value: Column[]): void
     * @summary Sets or gets the defining properties of the columns in a table.
     * @description
     *  Table [columns](#Column) do not store any data. They contain the
     *  properties, such as the `label` and `type`, that define a table column.
     *
     *  Setting the `columns` property sets the [columns](#Column)
     *  in the table.
     *
     *  Getting the `columns` property returns the current
     *  list of [columns](#Column) in the table.
     *
     *  You cannot modify the data array in-place. To add, change, or remove
     *  individual columns:
     *
     *    1. Store the value of the `columns` property in a variable.
     *    1. Make changes to the columns array.
     *    1. Reset the `columns` property with the modified array.
     * @snippet [Table-columns_get.es6=Get the list of columns and the first column's information]
     * @snippet [Table-columns_set.es6=Set the list of columns for a table]
     * @snippet [Table-add.es6=Add a column to a table]
     *  This example retrieves the columns of a table, adds a new column, and
     *  then overwrites the old options.
     * @type {$w.Table~Column[]}
     * @memberof $w.Table
     * @instance
     */
    get columns() {
        return Utils.getProp(this, 'columns');
    }

  /**
   * @member columns
   * @memberof $w.Table
   * @param {$w.Table~Column[]} columns
   */
    set columns(columns) {
        const compType = members._type.get(this).compType;
        const isArrayValid = validate({
            compType,
            functionName: 'columns',
            dataToValidate: {columns},
            compName: this.id
        });
        if (!isArrayValid) {
            return;
        }
        const arrItemsArrValid = _.every(columns, (v, i) => {
            return validate({
                compType,
                functionName: 'columns',
                dataToValidate: {column: v},
                shouldCheckMandatoryFields: true,
                itemIndex: i,
                compName: this.id
            });
        });
        if (!arrItemsArrValid) {
            return;
        }
        columns = nullIfNil(columns) ? columns : [];
        Utils.setProp(this, {
            columns: _.map(columns, (column) => {
                // Set column.visible to true by default
                return _.assign({visible: true}, column);
            })
        });
    }

    /* Taken out of docs in change back to dataFetcher property
     * @typedef {external:Object} $w.Table~RequestedData
     * @summary An object return by the handler set by the `onDataRequest` function.
     * @property {external:Object[]} pageRows An array of row data objects with field_name:value pairs.
     * @property {external:Number} totalRowsCount The number of total rows in all pages.
     * @snippet [Table-onDataRequest_set.es6=Set the function that fetches more data]
     * @snippet [Table-onDataRequest-scenario.es6=Fetch data from a static array]
     * @see [onDataRequest](#onDataRequest)
     */

    /* Taken out of docs in change back to dataFetcher property
     * @callback $w.DataRequestHandler
     * @summary Function that runs when a new page of table data is to be displayed.
     * @param {external:Number} startRow The index of the first row to get.
     * @param {external:Number} endRow The index after the last row to get.
     * @returns {Promise}
     * @fulfill {$w.Table~RequestedData} A row data object with field_name:value pairs.
     */

    /**
     * @member dataFetcher
     * @memberof $w.Table
     * @param {Function} rowRetrievalFunc
     */
    set dataFetcher(rowRetrievalFunc) {
        const compType = members._type.get(this).compType;
        if (!_.isNil(rowRetrievalFunc) && !validate({
            compType,
            functionName: 'dataFetcher',
            dataToValidate: {dataFetcherCallback: rowRetrievalFunc},
            compName: this.id
        })) {
            return this;
        }

        const RMI = members._RMI.get(this);
        const fetchDataEvent = RMI.getEventTypes().FETCH_DATA;

        Utils.setDataWithoutUpdate(this, {dataFetchHandlers: rowRetrievalFunc});
        const componentEvents = Utils.getEvents(this);
        const filteredBehaviors = _.filter(componentEvents, {eventType: fetchDataEvent});
        if(_.isEmpty(filteredBehaviors)) {
            Utils.onEvent(this, fetchDataEvent, dataFetchHandler(RMI));
        }

        Utils.setData(this, {
            numberOfDisplayedRows: 0,
            dataSource: {type: Enums.DataSourceType.DYNAMIC, revision: Date.now()}
        });
        if (this.pagination.type === Enums.PaginationType.NONE) {
            this.pagination = _.assign(this.pagination, {type: Enums.PaginationType.SCROLL});
        }
    }

    /**
     * @member dataFetcher
     * @label property
     * @syntax
     * get dataFetcher: function
     * set dataFetcher(rowRetrievalFunction: function): void
     * @summary Sets the function that is called when a new page of table data is to be displayed.
     * @description
     *  The `dataFetcher` property is typically used when you are populating
     *  your table with data from an external (non-Wix) data source. You set it
     *  to a function that fetches the data to display.
     *
     *  The function that runs to fetch a new page of data must conform to the
     *  following structure:
     *
     *    ```javascript
     *    function rowRetrievalFunction(startRow, endRow) {
     *      return new Promise( (resolve, reject) => {
     *        // Data retrieval code here
     *        if( retrievalSuccess ) {
     *          resolve( {
     *            pageRows: fetchedRows,
     *            totalRowsCount: numRows
     *          } );
     *        }
     *        else {
     *          reject();
     *        }
     *      } );
     *    }
     *    ```
     *
     *  Meaning, your function must:
     *
     *    + Take in two parameters:
     *        + `startRow` the index of the first row to get
     *        + `endRow` the index after the last row to get
     *    + Return a Promise which:
     *        + Either resolves to an object with the following properties:
     *            + `pageRows` is an array of column:value row data
     *            + `totalRowsCount` is the number of total rows in all pages
     *        + Or rejects
     *
     *
     *  Another way of populating your table with data programmatically is to
     *  use the [`rows`](#rows) property.
     * @snippet [Table-dataFetcher_set.es6=Set the function that fetches more data]
     * @snippet [Table-dataFetcher-scenario.es6=Fetch data from a static array]
     *  This example stores row data in a static array and uses `dataFetcher`
     *  to set the `getRows` function as the function to run when the table
     *  needs new data. The example assumes you have a table with ID `myTable`
     *  that contains two fields, `name` (type Text) and `num` (type Number).
     * @memberof $w.Table
     * @type {Function}
     * @instance
     * @see [rows](#rows)
     */
    get dataFetcher() {
        return Utils.getData(this, 'dataFetchHandlers');
    }

    /**
     * @member rows
     * @memberof $w.Table
     * @param {external:Object[]} data
     */
    set rows(data) {
        const compType = members._type.get(this).compType;
        const isArrayValid = validate({
            compType,
            functionName: 'rows',
            dataToValidate: {rows: data},
            compName: this.id
        });
        if (!isArrayValid) {
            return;
        }
        const arrItemsArrValid = _.every(data, (v, i) => {
            return validate({
                compType,
                functionName: 'rows',
                dataToValidate: {row: v},
                shouldCheckMandatoryFields: true,
                itemIndex: i,
                compName: this.id
            });
        });
        if (!arrItemsArrValid) {
            return;
        }
        data = nullIfNil(data) ? data : [];
        const linksTarget = Utils.getProp(this, 'linkTarget');
        const rowsData = _processData(data, this.columns, linksTarget);
        Utils.setData(this, {
            dataSource: {type: Enums.DataSourceType.STATIC, revision: Date.now()},
            rows: rowsData,
            revision: Date.now(),
            numberOfDisplayedRows: rowsData.length
        });
    }

    /**
     * @member rows
     * @label property
     * @syntax
     * get rows(): Object[]
     * set rows(value: Object[]): void
     * @summary Sets or gets the table's row data.
     * @description
     *  Setting the `rows` property sets the data of the table's rows. A table's
     *  data is stored as an array of objects where each object represents a row
     *  in the table.
     *
     *  The objects are composed of `"key":"value"` pairs where:
     *
     *  + `key` is:
     *    + A table column **Field Name** if the columns were set in the Editor
     *      using the **Manage Table** panel.
     *    + A [table column](#Column) `dataPath` if the columns were set in code
     *      using the [`columns`](#columns) property.
     *  + `value` is the value to be placed in that table column for the current row.
     *
     *
     *  For example, a simple array of table data may look like this:
     *
     *  ```javascript
     *  [
     *    {
     *      "firstName": "John",
     *      "lastName": "Doe",
     *      "email": "john.doe@somedomain.com",
     *      "image": "http://someImageUrl/john.jpg"
     *    },
     *    {
     *      "firstName": "Jane",
     *      "lastName": "Doe",
     *      "email": "jane.doe@somedomain.com",
     *      "image": "http://someImageUrl/jane.jpg"
     *    }
     *  ]
     *  ```
     *
     *  If the `rows` array contains objects with values whose keys are not
     *  table columns, those values do not appear in the table. For example,
     *  assuming the `rows` array shown above, if the table does not have an
     *  **email** column, the email values do not appear in the table.
     *
     *  Set `rows` to an empty array (`[]`) to remove the current rows.
     *
     *  Getting the `rows` property returns the current list of
     *  the table's row data. If the table is connected to a dataset, `rows`
     *  does not include the fields from the dataset items that are not
     *  connected to table columns.
     *
     *  To update an individual table row, use the [`updateRow()`](#updateRow)
     *  function.
     *
     *  To add or remove individual table rows:
     *
     *    1. Store the value of the `rows` property in a variable.
     *    1. Apply standard JavaScript array operators to the rows array.
     *    1. Reset the `rows` property with the new array.
     *
     *
     *  To set a table's row data based on a [`query`](wix-data.html#query)
     *  you perform using the [`wix-data`](wix-data.html) API:
     *
     *  1. Set the columns of the table in the Editor using the **Manage Table**
     *     panel or in code using the [`columns`](#columns) property to include
     *     the collection fields you want to display.
     *  1. Execute a [`query()`](wix-data.html#query) using the [`find()`]
     *     function.
     *  1. Set the tables `rows` to the [`items`](wix-data.WixDataQueryResult.html#items)
     *     of the query's [results](wix-data.WixDataQueryResult.html).
     *
     *
     *  Note that you can optionally add to, remove from, or modify the data
     *  returned from the query before using it to set the table's row data.
     * @snippet [Table-rows_get.es6=Get a table's row data]
     * @snippet [Table-rows_set.es6=Set a table's row data]
     * @snippet [Table-rows-add.es6=Add a row to a table]
     *  This example retrieves the rows of a table, adds a new row, and then
     *  overwrites the old rows.
     * @snippet [Table-rows_scenario-array.es6=Set a table's row data from an array]
     * @snippet [Table-rows_scenario-query.es6=Set a table's row data from a query]
     * @snippet [Table-rows_scenario-query-plus.es6=Set a table's row data from a query]
     *  This example populates a table that displays information about documents
     *  that are stored in a collection and allows users to download each document
     *  by clicking a link in the table.
     *
     *  When the page is loaded, we first set the columns of the table to match
     *  the fields of the collection that contains the documents, excluding the
     *  field that contains the actual document. We also create one additional
     *  column whose value is not taken from the collection. The values users
     *  see in that column are set later, but the links are set to take
     *  their URLs from a collection field.
     *
     *  Then a query is executed to retrieve information from the collection.
     *  Before setting the query results to be the table's row data, we add a
     *  property and value to each retrieved item. These are the values that
     *  are populated in the additional column that was created earlier.
     * @memberof $w.Table
     * @type {external:Object[]}
     * @instance
     */
    get rows() {
        return Utils.getData(this, 'rows');
    }

    // METHODS:

    /**
     * @function refresh
     * @syntax
     * function refresh(): void
     * @summary Fetches the most current data and updates the table.
     * @snippet [Table-refresh.es6=Refresh a table]
     * @memberof $w.Table
     */
    refresh() {
        const type = this.getDataSourceType();
        if (_.isUndefined(type)) {
            reporter.reportSdkError('Error: (Table.refresh) no data source is set');
            return;
        }
        Utils.setData(this, {dataSource: {
            type,
            revision: Date.now()
        }});
    }

    /**
     * @function onDataChange
     * @syntax
     * function onDataChange(handler: DataChangeEventHandler): void
     * callback DataChangeEventHandler(event: DataChangeEvent, $w: Function): void
     * @summary Adds an event handler that runs when the table's data is changed.
     * @description
     *  A table receives a `dataChange` event when the table's data is changed
     *  using the [`rows`](#rows) property or a data request triggers the
     *  [`onDataRequest` handler](#onDataRequest).
     * @note
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @param {$w.EventHandler} eventHandler The name of the function or
     *  the function expression to run when the table's data is changed.
     * @memberof $w.Table
     * @eventtype dataChange
     * @eventhandler
     */
    onDataChange(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onDataChange',
            dataToValidate: {onDataChange: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        return Utils.onEvent(this, RMI.getEventTypes().DATA_CHANGE, cb);
    }

    /**
     * @function onCellSelect
     * @syntax
     * function onCellSelect(handler: TableCellEventHandler): Table
     * callback TableCellEventHandler(event: TableCellEvent, $w: Function): void
     * @summary Adds an event handler that runs when a table cell is selected.
     * @description
     *  A table receives a [`TableCellEvent`]($w.TableCellEvent.html) when the
     *  table is set [set](https://support.wix.com/en/article/table-settings) in
     *  the Editor to select cells when clicked.
     * ![Clicking Selects Cells](images/table_settings_click_cells.png "Clicking Selects Cells")
     * @note
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @snippet [TableCellEvent-all.es6=Get the selected table cell information]
     * @param {$w.TableCellEventHandler} eventHandler The name of the function or
     *  the function expression to run when the cell is selected.
     * @returns {$w.Table} The table that triggered the event.
     * @memberof $w.Table
     * @eventtype cellSelect
     * @eventhandler
     */
    onCellSelect(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onCellSelect',
            dataToValidate: {onCellSelect: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        return Utils.onEvent(this, RMI.getEventTypes().CELL_SELECT, cb);
    }

    /**
     * @function onRowSelect
     * @syntax
     * function onRowSelect(handler: TableRowEventHandler): Table
     * callback TableRowEventHandler(event: TableRowEvent, $w: Function): void
     * @summary Adds an event handler that runs when a table row is selected.
     * @description
     *  A table receives a [`TableRowEvent`]($w.TableRowEvent.html) when the
     *  table is [set](https://support.wix.com/en/article/table-settings) in
     *  the Editor to select rows when clicked.
     * ![Clicking Selects Rows](images/table_settings_click_rows.png "Clicking Selects Rows")
     * @note
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @snippet [TableRowEvent-all.es6=Get the selected table cell information]
     * @snippet [TableRowEvent-rowData_dataset.es6=Get the full item information from the dataset]
     * @param {$w.TableRowEventHandler} eventHandler The name of the function or
     *  the function expression to run when the row is selected.
     * @returns {$w.Table} The table that triggered the event.
     * @memberof $w.Table
     * @eventtype rowSelect
     * @eventhandler
     */
    onRowSelect(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onRowSelect',
            dataToValidate: {onRowSelect: cb},
            compName: this.id
        })) {
            return;
        }
        const RMI = members._RMI.get(this);
        return Utils.onEvent(this, RMI.getEventTypes().ROW_SELECT, cb);
    }

    /**
     * @function selectRow
     * @syntax
     * function selectRow(index: Number): void
     * @summary Selects a table row by index.
     * @param {external:Number} index The number of the row to select.
     * @description
     *  The rows in a table are zero-based and do not include the table header.
     *  ![Table Row Numbering](images/table_row.png "Table Row Numbering")
     * @snippet [Table-selectRow.es6=Select the table's first row]
     * @memberof $w.Table
     */
    selectRow(index) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'selectRow',
            dataToValidate: {rowIndex: index},
            compName: this.id
        })) {
            return this;
        }
        if (!validateRange({
            functionName: 'selectRow',
            propertyName: 'rowIndex',
            value: index,
            minValue: 0,
            maxValue: this.rows.length - 1
        })) {
            return this;
        }
        RMI.executeCompBehavior(compId, 'modifySelection', {userSelection: {rowIndex: index}});
    }

    /**
     * @function updateRow
     * @syntax
     * function updateRow(index: Number, rowData: Object) : void
     * @summary Updates a the row data of a single row at the specified index.
     * @param {external:Number} index The number of the row to update.
     * @param {external:Object} rowData A row data object of field_name:value pairs where the keys are the table columns.
     * @description
     *  The rows in a table are zero-based and do not include the table header.
     *  ![Table Row Numbering](images/table_row.png "Table Row Numbering")
     * @snippet [Table-updateRow.es6=Update the table's first row]
     * @snippet [Table-updateRow-scenario.es6=Update the table's first row]
     * @memberof $w.Table
     */
    updateRow(index, rowData) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'updateRow',
            dataToValidate: {rowIndex: index, row: rowData},
            compName: this.id
        })) {
            return this;
        }

        const type = this.getDataSourceType();
        if (type === Enums.DataSourceType.DYNAMIC){
            executeModificationRowBehaviour.call(this, {
                rowDataDelta: {
                    revision: Date.now(),
                    index,
                    op: Enums.RowModification.UPDATE,
                    data: rowData
                }
            });
        } else {
            const rowsData = Utils.getData(this, 'rows');
            rowsData.splice(index, 1, rowData);
            Utils.setData(this, {
                dataSource: {type: Enums.DataSourceType.STATIC, revision: Date.now()},
                rows: rowsData,
                revision: Date.now()
            });
        }
    }

    toJSON() {
        const {id, type, global, rendered, sorting, filter, pagination, columns, rows} = this;
        return {id, type, global, rendered, sorting, filter, pagination, columns, rows};
    }
}

mixin(Table.prototype, HiddenCollapsedMixin.prototype);
mixin(Table.prototype, ClickableMixin.prototype);

export default Table;
